import { Check, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../Button';
import { TransitionOverlay } from '../shared/TransitionOverlay';

interface PaymentSuccessModalProps {
  paymentMethod: 'card' | 'spei' | 'link';
  onViewTicket: () => void;
  onBackToHome: () => void;
}

export const PaymentSuccessModal = ({
  paymentMethod,
  onViewTicket,
  onBackToHome,
}: PaymentSuccessModalProps) => {
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Tarjeta = confirmado inmediato
  // SPEI/Link = pendiente de confirmación
  const isPending = paymentMethod === 'spei' || paymentMethod === 'link';

  const handleBackToHome = () => {
    setIsTransitioning(true);
    
    // Transición suave de 400ms antes de navegar
    setTimeout(() => {
      onBackToHome();
    }, 400);
  };

  return (
    <>
      <TransitionOverlay isVisible={isTransitioning} message="Regresando al inicio..." />
      
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[24px] p-8 max-w-md w-full animate-scale-in">
          {/* Icon */}
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isPending ? 'bg-[#FFF5F0]' : 'bg-[#FFF5F0]'
          }`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isPending ? 'bg-[#FF6A2A]' : 'bg-[#FF6A2A]'
            }`}>
              {isPending ? (
                <AlertCircle className="w-10 h-10 text-white" strokeWidth={3} />
              ) : (
                <Check className="w-10 h-10 text-white" strokeWidth={3} />
              )}
            </div>
          </div>
          
          {/* Title & Description */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-[#111827] mb-2">
              {isPending ? 'Pendiente de confirmación de pago' : '¡Reserva confirmada!'}
            </h2>
            <p className="text-[#6B7280]">
              {isPending 
                ? 'Tu boleto se activará cuando el pago sea confirmado.'
                : 'Tu boleto ha sido enviado a tu correo electrónico. ¡Buen viaje!'
              }
            </p>
          </div>

          {/* Info Card for pending payments */}
          {isPending && (
            <div className="bg-[#FFF5F0] border border-[#FF6A2A] rounded-[14px] p-4 mb-6 text-left">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-[#FF6A2A] flex-shrink-0 mt-0.5" />
                <h3 className="font-semibold text-[#111827]">Información importante</h3>
              </div>
              <ul className="space-y-2 text-sm text-[#6B7280]">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6A2A] flex-shrink-0">•</span>
                  <span>Tu boleto se activará cuando el pago sea confirmado.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6A2A] flex-shrink-0">•</span>
                  <span>Tienes hasta 3 horas antes de la salida para completar el pago; después, el asiento se libera.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6A2A] flex-shrink-0">•</span>
                  <span>Puedes consultar las instrucciones de pago en el detalle del boleto.</span>
                </li>
              </ul>
            </div>
          )}
          
          {/* Actions */}
          <div className="space-y-3">
            <Button 
              variant="primary" 
              className="w-full py-3"
              onClick={onViewTicket}
            >
              Ver mi boleto
            </Button>
            <button 
              onClick={handleBackToHome}
              className="w-full text-[#6B7280] hover:text-[#111827] text-sm font-medium py-2"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};